/**
 * Pure Reducer and Materialization Engine for Captain Mesh Domain Protocol
 * 
 * Guarantees that any valid delivery permutation of events yields exactly the same
 * canonical state and conflict outcomes.
 */

import { EventTypes } from "./events";
import { getOrderStartConflictKey } from "./conflicts";

/**
 * Deterministic sort for concurrent sibling events (events with the same parent).
 * We sort by their logical identity so that every device orders them identically.
 */
function compareEvents(a, b) {
  const keyA = `${a.device_id}::${a.device_sequence}`;
  const keyB = `${b.device_id}::${b.device_sequence}`;
  if (keyA < keyB) return -1;
  if (keyA > keyB) return 1;
  return 0;
}

/**
 * Topologically sorts events based on causal parent relationships.
 * Concurrent siblings are sorted deterministically.
 * Discards (buffers) any events whose causal parent is missing from the set.
 */
export function sortCausalHistory(eventsArray) {
  const eventMap = new Map();
  const childrenMap = new Map(); // parent_event_id -> array of child events
  const roots = []; // events with parent_event_id === null

  // 1. Build adjacency list
  for (const ev of eventsArray) {
    eventMap.set(ev.event_id, ev);
    if (!childrenMap.has(ev.event_id)) {
      childrenMap.set(ev.event_id, []);
    }
    
    if (ev.parent_event_id === null) {
      roots.push(ev);
    } else {
      if (!childrenMap.has(ev.parent_event_id)) {
        childrenMap.set(ev.parent_event_id, []);
      }
      childrenMap.get(ev.parent_event_id).push(ev);
    }
  }

  // 2. Sort children lists deterministically to handle concurrent sibling revisions
  for (const [_, children] of childrenMap.entries()) {
    children.sort(compareEvents);
  }
  roots.sort(compareEvents);

  const sortedHistory = [];
  const visited = new Set();

  // 3. DFS to flatten the tree deterministically.
  // Only events reachable from a root (or a known snapshot root) are included.
  // Any event whose parent is missing won't be reachable, thus inherently "buffered".
  function visit(ev) {
    if (visited.has(ev.event_id)) return;
    visited.add(ev.event_id);
    sortedHistory.push(ev);

    const children = childrenMap.get(ev.event_id) || [];
    for (const child of children) {
      visit(child);
    }
  }

  for (const root of roots) {
    visit(root);
  }

  return { sortedHistory, allEventIds: Array.from(eventMap.keys()) };
}

/**
 * Applies a single event to the materialized state.
 */
function applyEvent(state, event) {
  switch (event.type) {
    case EventTypes.ORDER_STARTED:
      // Conflict semantics: Unique table claim. 
      // If the table is already claimed in this aggregate by another event,
      // the deterministic sort already decided the winner (the first one we processed).
      // If we see another ORDER_STARTED for the same aggregate, we ignore it 
      // OR we can explicitly mark it rejected.
      // Wait, the aggregate ID is the order ID. The table claim conflict is 
      // across DIFFERENT orders for the same table.
      // We handle cross-aggregate table claims in a higher level (the floor state).
      // Here we just initialize the order aggregate.
      if (!state.created_at) {
        const initialItems = {};
        if (event.payload.first_items && Array.isArray(event.payload.first_items)) {
          event.payload.first_items.forEach((item, index) => {
            const line_id = item.line_id || `initial-${index}`;
            initialItems[line_id] = {
              line_id: line_id,
              menu_item_id: item.menu_item_id,
              item_name: item.item_name || "Unknown Item",
              unit_price: item.unit_price || 0,
              quantity: item.quantity,
              notes: item.notes || "",
              status: "active"
            };
          });
        }
        return {
          ...state,
          id: event.aggregate_id,
          table_id: event.payload.table_id,
          created_at: event.created_at,
          status: "open",
          // The opening customer rides inside ORDER_STARTED (the client never
          // emits a separate CUSTOMER_ASSOCIATED on start). Dropping it here
          // made every summary show no guest and lost the association at
          // reconciliation.
          customer: event.payload.customer || null,
          staff_name: event.payload.staff_name || null,
          items: initialItems, // line_id -> item
          revision: event.result_revision,
          _last_event_id: event.event_id
        };
      }
      break;

    case EventTypes.CUSTOMER_ASSOCIATED:
      // Explicit conflict: if a customer is already associated, and this is a concurrent
      // sibling associating a DIFFERENT customer, we flag a conflict.
      if (state.customer && state.customer.id !== event.payload.customer.id) {
        return {
          ...state,
          customer_conflict: true, // Captain UI must resolve
          revision: event.result_revision,
          _last_event_id: event.event_id
        };
      }
      return {
        ...state,
        customer: event.payload.customer,
        revision: event.result_revision,
        _last_event_id: event.event_id
      };

    case EventTypes.ITEM_ADDED:
      return {
        ...state,
        items: {
          ...state.items,
          [event.payload.line_id]: {
            line_id: event.payload.line_id,
            menu_item_id: event.payload.menu_item_id || event.payload.item_id,
            item_name: event.payload.name || "Unknown Item",
            unit_price: event.payload.price || 0,
            quantity: event.payload.quantity,
            notes: event.payload.notes || "",
            status: "active"
          }
        },
        revision: event.result_revision,
        _last_event_id: event.event_id
      };

    case EventTypes.ITEM_QUANTITY_DELTA: {
      const item = state.items[event.payload.line_id];
      if (!item || item.status === "removed") return state; // Commutative skip if removed
      return {
        ...state,
        items: {
          ...state.items,
          [event.payload.line_id]: {
            ...item,
            quantity: Math.max(0, item.quantity + event.payload.delta)
          }
        },
        revision: event.result_revision,
        _last_event_id: event.event_id
      };
    }

    case EventTypes.ITEM_NOTE_SET: {
      // A line the kitchen has been asked for keeps its note — the server
      // refuses the change there too, and the screen must show what the KOT says.
      const item = state.items[event.payload.line_id];
      if (!item || item.status !== "active" || item.kot_intent_event_id) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [event.payload.line_id]: { ...item, notes: String(event.payload.notes || "").trim() }
        },
        revision: event.result_revision,
        _last_event_id: event.event_id
      };
    }

    case EventTypes.ITEM_REMOVED: {
      const item = state.items[event.payload.line_id];
      if (!item) return state;
      return {
        ...state,
        items: {
          ...state.items,
          [event.payload.line_id]: {
            ...item,
            status: "removed" // Tombstone
          }
        },
        revision: event.result_revision,
        _last_event_id: event.event_id
      };
    }

    case EventTypes.KOT_REQUESTED: {
      // Idempotent intent, keyed on intent_id so duplicates from concurrent
      // siblings are ignored.
      const intents = state.kot_intents || {};
      if (intents[event.payload.intent_id]) break;

      // Stamp the lines the intent covers. `_sync_state` rides on the stored
      // event record (not the wire envelope): "server_acked" here means the
      // backend recorded this intent in mesh_reconciled_events, i.e. a real KOT
      // was issued for these lines. That single fact drives the whole per-line
      // truth ladder — Pending KOT → Pending Server → Sent — and it is why
      // acknowledgement must trigger a re-materialize: the fold has to re-read
      // the flag. Legacy intents carried no line list; they covered every line
      // that existed, so that is what the fallback stamps.
      const acked = event._sync_state === "server_acked";
      const coveredIds = event.payload.line_ids
        || event.payload.items
        || Object.values(state.items || {})
             .filter(i => i.status === "active" && !i.kot_intent_event_id)
             .map(i => i.line_id);
      const items = { ...state.items };
      for (const lid of coveredIds) {
        if (items[lid] && items[lid].status === "active") {
          items[lid] = {
            ...items[lid],
            kot_intent_event_id: event.event_id,
            kot_acked: acked,
          };
        }
      }
      return {
        ...state,
        items,
        kot_intents: { ...intents, [event.payload.intent_id]: coveredIds },
        revision: event.result_revision,
        _last_event_id: event.event_id
      };
    }

    // Additional types can be implemented similarly...
  }

  // If no state change, just advance the revision marker to track causal progress
  return {
    ...state,
    revision: event.result_revision,
    _last_event_id: event.event_id
  };
}

/**
 * Computes the materialized state of a single aggregate from its causal history.
 */
export function materializeAggregate(eventsArray) {
  const { sortedHistory } = sortCausalHistory(eventsArray);
  
  let state = {};
  for (const ev of sortedHistory) {
    state = applyEvent(state, ev);
  }
  return state;
}

/**
 * The pure reducer.
 * Receives the current `MeshState` (which contains raw events and materialized views)
 * and a new event, and returns the strictly deterministic updated `MeshState`.
 */
export function reduce(meshState, event) {
  const nextEvents = { ...meshState.events, [event.event_id]: event };
  
  // Re-materialize the affected aggregate purely from history
  const aggregateEvents = Object.values(nextEvents).filter(e => e.aggregate_id === event.aggregate_id);
  const { sortedHistory } = sortCausalHistory(aggregateEvents);
  
  let aggregateState = {};
  for (const ev of sortedHistory) {
    aggregateState = applyEvent(aggregateState, ev);
  }

  return {
    ...meshState,
    events: nextEvents,
    materialized: {
      ...meshState.materialized,
      [event.aggregate_id]: aggregateState
    }
  };
}

/**
 * Cross-aggregate conflict resolution: Resolves Table Claims.
 * Identifies which order actually owns the table if multiple aggregates
 * try to claim the same table concurrently.
 */
export function resolveTableFloor(meshState) {
  const tableClaims = {}; // table_id -> array of ORDER_STARTED events

  // Find all root ORDER_STARTED events
  for (const event of Object.values(meshState.events)) {
    if (event.type === EventTypes.ORDER_STARTED) {
      const tId = event.payload.table_id;
      if (!tableClaims[tId]) tableClaims[tId] = [];
      tableClaims[tId].push(event);
    }
  }

  const floorState = {};
  
  for (const [tableId, claims] of Object.entries(tableClaims)) {
    // Sort claims using the explicitly defined canonical total-order key
    claims.sort((a, b) => {
      const keyA = getOrderStartConflictKey(a);
      const keyB = getOrderStartConflictKey(b);
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });

    // The winner is the first one
    const winner = claims[0];
    floorState[tableId] = {
      occupying_order_id: winner.aggregate_id,
      rejected_order_ids: claims.slice(1).map(e => e.aggregate_id)
    };
  }

  return floorState;
}
